/**
 * Turns a giftee's interests into reco cards backed by the static Amazon catalog
 * (`data/amazonGiftCatalog.js`). Interest classification always goes through
 * `gifteeInterests.js` so profile, wizard and carousels stay on one catalog.
 */
import {
  AMAZON_GIFT_CATALOG,
  CATALOG_BY_ASIN,
  CROWD_PLEASERS,
} from "../data/amazonGiftCatalog.js";
import {
  customInterestsFromSet,
  orderedSelectedPresetInterests,
  parseInterestsFromGiftee,
} from "./gifteeInterests.js";
import { amazonProductUrl, amazonUrlForRec } from "./amazonLinks.js";

/**
 * Older labels (emulator seed data and pre-rewrite giftees) that are no longer
 * chips in `INTEREST_GROUPS`. Mapping them keeps those giftees on-theme instead of
 * dropping them into the generic fallback deck.
 */
export const LEGACY_INTEREST_ALIASES = {
  active: "Running",
  beauty: "Skincare",
  books: "Fiction",
  cooking: "Kitchen Gadgets",
  crafts: "DIY",
  fashion: "Accessories",
  fitness: "Strength",
  food: "Snacks",
  gadgets: "Desk Setup",
  garden: "Gardening",
  home: "Organization",
  kitchen: "Kitchen Gadgets",
  movies: "Audio",
  music: "Audio",
  pets: "Dogs",
  reading: "Fiction",
  sports: "Strength",
  summer: "Outdoors",
  tech: "Desk Setup",
  tools: "DIY",
  wellness: "Recovery",
};

const DEFAULT_LIMIT = 6;
/** Budget headroom: a slightly-over-budget standout still beats an empty slot. */
const BUDGET_SLACK = 1.15;

function aliasFor(label) {
  return LEGACY_INTEREST_ALIASES[label.trim().toLowerCase()] ?? null;
}

/**
 * Catalog interest labels for a giftee: selected presets in catalog order, then
 * any custom labels that alias onto a catalog interest.
 */
export function catalogInterestsForGiftee(giftee) {
  const selected = parseInterestsFromGiftee(giftee);
  const labels = orderedSelectedPresetInterests(selected).filter(
    (label) => AMAZON_GIFT_CATALOG[label]?.length,
  );

  for (const custom of customInterestsFromSet(selected)) {
    const alias = aliasFor(custom);
    if (alias && AMAZON_GIFT_CATALOG[alias]?.length && !labels.includes(alias)) {
      labels.push(alias);
    }
  }
  return labels;
}

/** Stable per-giftee rotation so two giftees sharing an interest lead with different items. */
function rotationOffset(seed, length) {
  if (length <= 1) return 0;
  const str = typeof seed === "string" ? seed : String(seed ?? "");
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) % 100000;
  }
  return hash % length;
}

function rotate(list, offset) {
  if (!offset) return list;
  return [...list.slice(offset), ...list.slice(0, offset)];
}

function budgetCeiling(giftee) {
  const max = Number(giftee?.budgetMax);
  if (!Number.isFinite(max) || max <= 0) return null;
  return max * BUDGET_SLACK;
}

export function formatReviewCount(count) {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${n.toLocaleString("en-US")} Reviews`;
}

/** Catalog product -> the card shape `ReminderRecoCarousel` / `GifteeRecoSwipeCard` render. */
export function catalogProductToCard(product) {
  return {
    id: `catalog-${product.asin}`,
    asin: product.asin,
    title: product.title,
    fullTitle: product.fullTitle,
    brand: product.brand,
    priceText: product.priceText,
    priceValue: product.priceValue,
    source: "via Amazon",
    tags: product.interests,
    rating: product.rating,
    reviewCount: formatReviewCount(product.reviewCount),
    imageUrl: product.imageUrl,
    productUrl: amazonProductUrl(product.asin),
  };
}

/**
 * Round-robins one product per interest per pass so a multi-interest giftee gets a
 * spread of categories before a second item from any single interest.
 */
function interleaveByInterest(labels, { limit, ceiling, seed }) {
  const queues = labels.map((label) =>
    rotate(
      AMAZON_GIFT_CATALOG[label] ?? [],
      rotationOffset(`${seed}:${label}`, AMAZON_GIFT_CATALOG[label]?.length ?? 0),
    ),
  );

  const picked = [];
  const overflow = [];
  const seen = new Set();
  const depth = Math.max(0, ...queues.map((queue) => queue.length));

  for (let round = 0; round < depth; round += 1) {
    for (const queue of queues) {
      const product = queue[round];
      if (!product || seen.has(product.asin)) continue;
      seen.add(product.asin);
      if (ceiling !== null && product.priceValue > ceiling) {
        overflow.push(product);
      } else {
        picked.push(product);
      }
    }
  }

  return [...picked, ...overflow].slice(0, limit);
}

/**
 * Gift ideas for a giftee, newest interest data first: catalog interests drive the
 * mix, `budgetMax` demotes (but never fully hides) pricier picks, and giftees with
 * no catalog interests fall back to the crowd-pleaser deck.
 */
export function getSuggestionsForGiftee(giftee, { limit = DEFAULT_LIMIT } = {}) {
  const labels = catalogInterestsForGiftee(giftee);
  const seed = giftee?.id ?? giftee?.name ?? "giftee";
  const ceiling = budgetCeiling(giftee);

  if (labels.length === 0) {
    const fallback = rotate(
      CROWD_PLEASERS,
      rotationOffset(seed, CROWD_PLEASERS.length),
    );
    const affordable = ceiling
      ? [
          ...fallback.filter((p) => p.priceValue <= ceiling),
          ...fallback.filter((p) => p.priceValue > ceiling),
        ]
      : fallback;
    return affordable.slice(0, limit).map(catalogProductToCard);
  }

  const rotatedLabels = rotate(labels, rotationOffset(seed, labels.length));
  return interleaveByInterest(rotatedLabels, { limit, ceiling, seed }).map(
    catalogProductToCard,
  );
}

/**
 * Firestore `savedRecommendations` doc -> card shape, backfilling catalog data when
 * the doc was saved from the catalog so saved cards keep their image and link.
 */
export function savedRecommendationToCard(rec, index = 0) {
  const catalogMatch = rec?.asin ? CATALOG_BY_ASIN.get(rec.asin) : null;
  const merged = { ...(catalogMatch ? catalogProductToCard(catalogMatch) : {}) };

  const tags = Array.isArray(rec?.tags) && rec.tags.length > 0 ? rec.tags : merged.tags;
  const reviewCount =
    typeof rec?.reviewCount === "number"
      ? formatReviewCount(rec.reviewCount)
      : rec?.reviewCount || merged.reviewCount;

  const card = {
    ...merged,
    id: rec?.id || merged.id || `rec-${index}`,
    savedDocId: rec?.id,
    asin: rec?.asin || merged.asin,
    title: rec?.title || merged.title || "Gift idea",
    fullTitle: rec?.fullTitle || merged.fullTitle || rec?.title,
    brand: rec?.brand || merged.brand,
    priceText: rec?.priceText || merged.priceText || "$--.--",
    source: rec?.source || "via Amazon",
    tags: Array.isArray(tags) ? tags : [],
    rating: rec?.rating ?? merged.rating,
    reviewCount,
    imageUrl:
      (typeof rec?.imageUrl === "string" && rec.imageUrl.trim()) ||
      (typeof rec?.imageSrc === "string" && rec.imageSrc.trim()) ||
      (typeof rec?.thumbnailUrl === "string" && rec.thumbnailUrl.trim()) ||
      merged.imageUrl,
    sizes: Array.isArray(rec?.sizes) && rec.sizes.length > 0 ? rec.sizes : undefined,
    colors: Array.isArray(rec?.colors) && rec.colors.length > 0 ? rec.colors : undefined,
  };

  card.productUrl = amazonUrlForRec({ ...card, productUrl: rec?.productUrl });
  return card;
}

/**
 * Cards for a carousel: saved recommendations when the giftee has them, otherwise
 * interest-driven catalog suggestions.
 */
export function recommendationCardsForGiftee(
  giftee,
  savedRecommendations,
  { limit = DEFAULT_LIMIT } = {},
) {
  if (Array.isArray(savedRecommendations) && savedRecommendations.length > 0) {
    return savedRecommendations.map(savedRecommendationToCard);
  }
  return getSuggestionsForGiftee(giftee, { limit });
}
